import type { ScreenerReq, ScreenerRespEntity } from '@revelio/data-access';

import type { Filter } from '../../../filters/types/filters';
import { adaptFilterStateForScreener } from '../../Chart/Table/adapt-filter-state-for-screener';
import { getSegmentsText } from './getSegmentsText';

export type ScreenerTableRow = {
  [key: string]: number | string | boolean | null | undefined;
};

// Similar to old screener's getFilterInfo, but adapted for CustomDash filter structure
const getFilterInfo = (
  key: string,
  index: number,
  filters: Omit<ScreenerReq, 'pagination'>,
  legacyState: ReturnType<typeof adaptFilterStateForScreener>
): string => {
  const segmentsRecord = filters.segments;
  if (!segmentsRecord) return `${index + 1}`;

  const segmentEntry = Object.entries(segmentsRecord).find(
    ([segmentKey]) => segmentKey === key
  );
  const segments = segmentEntry?.[1];

  if (!Array.isArray(segments) || segments.length <= index) {
    return `${index + 1}`;
  }

  const treeItems = legacyState.filters.sub_filters
    .filter((subFilter) => subFilter.filter.name === key)
    .at(index)?.treeItems;

  return Object.keys(treeItems || {}).length
    ? ` (${getSegmentsText(treeItems)})`
    : '(All Employees)';
};

const isSegmentWithoutSelections = (
  key: string,
  index: number,
  filters: Omit<ScreenerReq, 'pagination'>,
  legacyState: ReturnType<typeof adaptFilterStateForScreener>
): boolean => {
  const segmentsRecord = filters.segments;
  if (!segmentsRecord) {
    return false;
  }

  const segmentEntry = Object.entries(segmentsRecord).find(
    ([segmentKey]) => segmentKey === key
  );
  const segments = segmentEntry?.[1];
  if (!Array.isArray(segments) || segments.length <= index) {
    return false;
  }

  const treeItems = legacyState.filters.sub_filters
    .filter((subFilter) => subFilter.filter.name === key)
    .at(index)?.treeItems;

  return Object.keys(treeItems || {}).length === 0;
};

const transformCompanyInfo = (
  entity: ScreenerRespEntity
): ScreenerTableRow | null => {
  const companyInfo = entity.entity_info?.company_info;

  if (!companyInfo) {
    return null;
  }

  const companyFields: ScreenerTableRow = {};
  Object.entries(companyInfo).forEach(([key, value]) => {
    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean' ||
      value === null ||
      value === undefined
    ) {
      companyFields[key] = value;
    }
  });

  const genderMapping: Record<string, string> = {
    founder_male: 'Male',
    founder_female: 'Female',
  };

  const genderOptions: string[] = [];
  Object.entries(genderMapping).forEach(([key, label]) => {
    const companyValue = companyInfo[key as keyof typeof companyInfo];
    if (typeof companyValue === 'boolean' && companyValue) {
      genderOptions.push(label);
    }
  });

  const genderValue = genderOptions.length > 0 ? genderOptions.join(', ') : '';

  const ethnicityMapping: Record<string, string> = {
    founder_api: 'Asian/Pacific Islander',
    founder_black: 'Black',
    founder_hispanic: 'Hispanic',
    founder_native: 'Native American',
    founder_white: 'White',
    founder_multiple: 'Multiple',
  };

  const ethnicityOptions: string[] = [];
  Object.entries(ethnicityMapping).forEach(([key, label]) => {
    const companyValue = companyInfo[key as keyof typeof companyInfo];
    if (typeof companyValue === 'boolean' && companyValue) {
      ethnicityOptions.push(label);
    }
  });

  const ethnicityValue =
    ethnicityOptions.length > 0 ? ethnicityOptions.join(', ') : '';

  const location = (() => {
    const { hq_country, hq_region, hq_metro_area } = companyInfo;
    const parts = [hq_metro_area, hq_country, hq_region].filter(
      (part): part is string =>
        Boolean(
          part &&
            typeof part === 'string' &&
            part.trim() !== '' &&
            part.toLowerCase() !== 'empty'
        )
    );
    return parts.join(', ');
  })();

  return {
    ...companyFields,
    founder_gender: genderValue,
    founder_ethnicity: ethnicityValue,
    hq_location: location,
    hq_metro_area: companyInfo.hq_metro_area,
    hq_country: companyInfo.hq_country,
  };
};

const transformEntity =
  (
    filters: Omit<ScreenerReq, 'pagination'>,
    legacyState: ReturnType<typeof adaptFilterStateForScreener>
  ) =>
  (entity: ScreenerRespEntity): ScreenerTableRow => {
    const topLevelFields: ScreenerTableRow = {};
    const usedKeys = new Set<string>();

    Object.entries(entity).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((val, index) => {
          if (
            index > 0 &&
            isSegmentWithoutSelections(key, index - 1, filters, legacyState)
          ) {
            return;
          }

          const filterInfo =
            index === 0
              ? ''
              : getFilterInfo(key, index - 1, filters, legacyState);
          const baseKey = index === 0 ? key : `${key}_${filterInfo}`;
          const normalizedKey = baseKey.replace(/_+$/, '');

          let uniqueKey = normalizedKey;
          let counter = 1;
          // Collision detection: ensure unique keys by appending counter suffix when duplicates exist.
          // Rationale: When transforming array values, keys are generated as `${key}_${filterInfo}`.
          // If multiple array items share the same filterInfo (e.g., both are "(All Employees)"),
          // they would generate identical keys, causing later values to overwrite earlier ones.
          // This loop appends a counter (_1, _2, etc.) to ensure each value gets a unique key.
          while (usedKeys.has(uniqueKey)) {
            uniqueKey = `${normalizedKey}_${counter}`;
            counter++;
          }
          usedKeys.add(uniqueKey);

          topLevelFields[uniqueKey] = val;
        });
      } else if (
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean' ||
        value === null ||
        value === undefined
      ) {
        usedKeys.add(key);
        topLevelFields[key] = value;
      }
    });

    const companyInfo = transformCompanyInfo(entity);

    if (companyInfo) {
      return { ...topLevelFields, ...companyInfo };
    }

    return topLevelFields;
  };

export const transformScreenerDataForTable = (
  entities: ScreenerRespEntity[],
  filters: Omit<ScreenerReq, 'pagination'>,
  effectiveFilters: Filter[]
): ScreenerTableRow[] => {
  if (!entities || entities.length === 0) {
    return [];
  }

  const legacyState = adaptFilterStateForScreener(effectiveFilters, filters);

  return entities.map(transformEntity(filters, legacyState));
};
